// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xScreen } from "@pacific/XDL/Utilities";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

interface Props extends WidgetProps {}

export interface BrandDetailsImageStyles {
    productLink?: LinkPresentationProps;
    externalLink?: LinkPresentationProps;
    linksWrapper?: InjectableCss;
}

export const linksStyles: BrandDetailsImageStyles = {
    linksWrapper: {
        css: css`
            display: flex;

            ${xScreen("lte", "sm")} {
                flex-direction: column;
            }
        `,
    },
    productLink: {
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                white-space: nowrap;
                ${xScreen("lte", "sm")} {
                    margin-bottom: 10px;
                }
            `,
        },
    },
    externalLink: {
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                white-space: nowrap;
                margin-left: 20px;
                ${xScreen("lte", "sm")} {
                    margin-left: 0;
                }
            `,
        },
    },
};

const styles = linksStyles;

const BrandDetailsLinks: FC<Props> = () => {
    const { value: brand } = useContext(BrandStateContext);
    if (!brand) {
        return null;
    }

    return (
        <StyledWrapper {...styles.linksWrapper}>
            <Link
                data-test-selector="brandToProductPageLink"
                href={brand.productListPagePath}
                {...styles.productLink}
                aria-label={`shop all ${brand.name} products`}
            >
                {translate("Shop All {0} Products").replace("{0}", brand.name)}
            </Link>
            {brand.externalUrl && (
                <Link
                    data-test-selector="brandExternalUrlLink"
                    href={brand.externalUrl}
                    {...styles.externalLink}
                    aria-label={`visit ${brand.name} website`}
                >
                    {translate("Visit {0} Website").replace("{0}", brand.name)}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BrandDetailsLinks,
    definition: {
        group: "Brand Details",
        displayName: "Brand Details Links",
        allowedContexts: ["BrandDetailsPage"],
    },
};

export default widgetModule;
